import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../recoil/pub.atom";
import { TimerInfoState } from "../../timer-controller";
import { useTimer } from "../../timer-controller/hooks/use-timer";
import { getImages } from "../../../api/pub-grade";
import { getStructuresById } from "../../../api/tournament";
import { convertToKoreanNumber } from "../../../utils/timer";
import { calculateTimes } from "../../timer-controller/hooks/timer";

export const useClock = () => {
  const { id } = useParams();
  const tournamentId = id?.replace(/[^0-9]/g, "");
  const pub = useRecoilValue(pubState);
  const [subPrizeFont, setSubPrizeFont] = useState(0);

  useEffect(() => {
    const font = localStorage.getItem("subPrizeFont");
    if (font) {
      setSubPrizeFont(+font);
    }
  }, []);

  const [timeInfo, setTimeInfo] = useState<TimerInfoState>({
    elapsedTimeMs: null,
    elapsedTime: "00:00:00",
    currentLevel: null,
    remainingTimeInCurrentLevel: "00:00",
    timeUntilNextBreak: "00:00",
    elapsedTimeInCurrentLevel: "00분00초",
    elapsedTimeInCurrentLevelMs: null,
    remainingTimeInCurrentLevelMs: null,
  });

  // 매장 이미지
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);

  // 유저 정보 및 통계
  const [userCount, setUserCount] = useState<number>(0);
  const [reBuyInUserCount, setReBuyInUserCount] = useState<number>(0);
  const [entryUserCount, setEntryUserCount] = useState<number>(0);
  const [adOnUserCount, setAdOnUserCount] = useState<number>(0);
  const [stack, setStack] = useState(0);
  const [avgStack, setAvgStack] = useState(0);

  // 서버 시간 차이
  const [diffTime, setDiffTime] = useState<null | number>(null);

  // 광고 표시 여부
  const [isAd, setIsAd] = useState<boolean>(false);

  const { connectSocket, data, setData, canUsed } = useTimer(
    tournamentId,
    pub?.id,
  );

  // 랭킹 페이징 관련
  const [visibleRanks, setVisibleRanks] = useState<number[]>([
    4, 5, 6, 7, 8, 9, 10,
  ]);
  const groupSize = 7;

  // 권한 체크 및 프리미엄 등급 체크
  useEffect(() => {
    if (!canUsed) {
      alert("다른 매장의 토너먼트에 접근할 수 없습니다.");
      window.close();
    }
  }, [canUsed]);

  // 소켓 연결
  useEffect(() => {
    connectSocket();
  }, [tournamentId]);

  // 매장 이미지 호출
  useEffect(() => {
    const fetchImages = async () => {
      if (!pub) return;
      try {
        const res = await getImages(pub.id);
        setCoverImage(res.coverImageUrl);
        setBackgroundImage(res.backgroundImageUrl);
      } catch (e) {
        console.error("매장 이미지 로드 실패:", e);
      }
    };
    fetchImages();
  }, [pub]);

  // 데이터 변경 시 통계 업데이트 및 시간 차이 계산
  useEffect(() => {
    if (!data) return;

    // 유저 수 및 스택 계산
    const uCount = data.userCount || 0;
    const rbCount = data.reBuyInUserCount || 0;
    const eCount = data.entryUserCount || 0;
    const aCount = data.adOnUserCount || 0;
    const allUserCount = rbCount + eCount + aCount;

    setUserCount(uCount);
    setReBuyInUserCount(rbCount);
    setEntryUserCount(eCount);
    setAdOnUserCount(aCount);

    const reBuyInStack = rbCount * (data.reBuyInChip || 0);
    const entryStack = eCount * (data.buyInChip || 0);
    const adOnStack = aCount * (data.adOnChip || 0);
    const earlyStack = data.earlyInChipCount || 0;

    const totalStack = reBuyInStack + entryStack + adOnStack + earlyStack;

    setStack(totalStack);
    setAvgStack(uCount > 0 ? Math.floor(totalStack / uCount) : 0);

    // 서버 시간 차이 계산
    if (data.timerDto?.now && diffTime === null) {
      const now = new Date();
      setDiffTime(now.getTime() - new Date(data.timerDto.now).getTime());
    }

  }, [data, diffTime]);

  // 타이머 업데이트
  useEffect(() => {
    const interval = setInterval(() => {
      if (!data?.timerDto || !data?.structure) return;

      const updatedTimeInfo = calculateTimes(
        data.timerDto,
        data.structure,
        diffTime,
      );
      setTimeInfo(updatedTimeInfo);
    }, 300);

    return () => clearInterval(interval);
  }, [data, diffTime]);

  // 광고 자동 표시
  useEffect(() => {
    if (
      timeInfo.currentLevel?.level === null &&
      timeInfo.elapsedTimeMs !== null
    ) {
      setIsAd(true);
    }
  }, [timeInfo.currentLevel, timeInfo.elapsedTimeMs]);

  // 랭킹 페이징
  useEffect(() => {
    if (!data || data.moneyInChip.length <= 11) return;

    const totalRanks = data.moneyInChip.length;
    const totalGroups = Math.ceil((totalRanks - 3) / groupSize);
    let currentGroupIndex = 1;

    const interval = setInterval(() => {
      setVisibleRanks(() => {
        const groupStart = 4 + currentGroupIndex * groupSize;
        const groupEnd = Math.min(groupStart + groupSize - 1, totalRanks);
        currentGroupIndex = (currentGroupIndex + 1) % totalGroups;

        return Array.from(
          { length: groupEnd - groupStart + 1 },
          (_, i) => groupStart + i,
        );
      });
    }, 9000);

    return () => clearInterval(interval);
  }, [data?.moneyInChip.length]);

  // 텍스트 변환 함수
  const getNowBlind = () => {
    if (!data?.structure) return "";
    const currentOrd = timeInfo.currentLevel?.ord;
    if (currentOrd === undefined) {
      // 시작 전
      const first = data.structure[0];
      return `${convertToKoreanNumber(first.smallBlind)}/${convertToKoreanNumber(first.bigBlind)}(${convertToKoreanNumber(first.ante)})`;
    }

    const nowLevel = data.structure[currentOrd - 1];
    if (nowLevel.type === "BREAK") return "BREAK";

    return `${convertToKoreanNumber(nowLevel.smallBlind)}/${convertToKoreanNumber(nowLevel.bigBlind)}(${convertToKoreanNumber(nowLevel.ante)})`;
  };

  const getNextBlind = () => {
    if (!data?.structure) return "";
    const currentOrd = timeInfo.currentLevel?.ord;
    if (currentOrd === undefined) {
      // 시작 전의 다음 레벨
      const second = data.structure[1];
      return `${convertToKoreanNumber(second.smallBlind)}/${convertToKoreanNumber(second.bigBlind)}(${convertToKoreanNumber(second.ante)})`;
    }

    if (currentOrd >= data.structure.length) return "THE END";

    const nextLevel = data.structure[currentOrd];
    if (nextLevel.type === "BREAK") return "BREAK";

    return `${convertToKoreanNumber(nextLevel.smallBlind)}/${convertToKoreanNumber(nextLevel.bigBlind)}(${convertToKoreanNumber(nextLevel.ante)})`;
  };

  const findNextLevel = () => {
    if (timeInfo.currentLevel?.type !== "BREAK" || !data?.structure) return 0;
    const nextOrd = timeInfo.currentLevel.ord;
    if (nextOrd < data.structure.length) {
      return data.structure[nextOrd].level || 0;
    }
    return 0;
  };

  const getData = async () => {
    if (!tournamentId) return;
    try {
      const result = await getStructuresById(
        +tournamentId?.replace(/[^0-9]/g, ""),
      );
      setData(result);
    } catch (e) {
      console.error("데이터 로드 실패:", e);
    }
  };

  return {
    timeInfo,
    userCount,
    reBuyInUserCount,
    entryUserCount,
    adOnUserCount,
    stack,
    avgStack,
    pub,
    coverImage,
    backgroundImage,
    isAd,
    setIsAd,
    data,
    setData,
    canUsed,
    visibleRanks,
    getNextBlind,
    getNowBlind,
    findNextLevel,
    getData,
    diffTime,
    subPrizeFont,
  };
};
